<!--
  ReferenceSelect.vue

  Dropdown picker for single-value reference fields whose option set is
  bounded and pre-loaded in a store (schema, type, user). Parallel to
  `ReferenceInput`/`ReferenceCell` but UX is a native `<select>` instead of
  chips + autocomplete — appropriate when the full list fits in a menu.

  Dispatches on `field.type` to pick the right options source and value
  shape. Adding a new reference type means adding one entry to
  `TYPE_DISPATCH` below.

  Single-select only — multi-select reference fields stay on
  `ReferenceInput`/`ReferenceCell`.
-->
<template>
  <div class="reference-select">
    <span v-if="leadingIcon" class="icon is-left" :class="sizeClass">
      <font-awesome-icon :icon="leadingIcon" />
    </span>
    <div class="select" :class="sizeClass">
      <select
        :value="modelValue ?? ''"
        :disabled="readonly"
        :class="{ 'has-placeholder': modelValue == null, 'has-leading-icon': !!leadingIcon }"
        @change="handleChange"
      >
        <option v-if="allowNone" value="">{{ noneLabel }}</option>
        <option v-else-if="modelValue == null" value="" disabled>{{ placeholder }}</option>

        <template v-if="dispatch.groups.length > 0">
          <template v-for="group in dispatch.groups" :key="group.key">
            <optgroup v-if="group.label" :label="group.label">
              <option v-for="opt in group.options" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </optgroup>
            <template v-else>
              <option v-for="opt in group.options" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </template>
          </template>
        </template>

        <option v-else value="" disabled>No options available</option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { storeToRefs } from "pinia"
import type { AugerField } from "@auger/core/fields"
import { useSchemasStore } from "@/stores/schemas"
import { getTypeIcon, useTypesStore } from "@/stores/types"
import { useUsersStore } from "@/stores/users"

type OptionValue = number | string

interface SelectOption {
  value: OptionValue
  label: string
}

interface OptionGroup {
  key: string
  label: string | null
  options: SelectOption[]
}

interface TypeDispatch {
  // Reactive computed of grouped options for this type. Each dispatch
  // pulls from its own store and applies field-driven filtering (e.g.,
  // schema filters by `field.subtype === record_type`).
  groups: OptionGroup[]
  // Coerce the string value coming off the <select> element back into
  // the storage shape for this type (number for ids, string for type
  // names).
  parse: (raw: string) => OptionValue | null
  placeholder: string
  // Optional leading-icon resolver for the currently-selected value.
  // Native <option> can't render icons, so we render one outside the
  // <select> for the selected value only — matches legacy TypeSelect.
  icon?: (value: OptionValue | null) => string | null
}

const props = withDefaults(
  defineProps<{
    modelValue?: OptionValue | null
    item?: Record<string, unknown> | null
    field?: AugerField
    readonly?: boolean
    size?: string
    placeholder?: string
    allowNone?: boolean
    noneLabel?: string
  }>(),
  {
    modelValue: null,
    item: null,
    field: undefined,
    readonly: false,
    size: "normal",
    placeholder: undefined,
    allowNone: true,
    noneLabel: "None",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: OptionValue | null]
}>()

const sizeClass = computed(() => (props.size ? `is-${props.size}` : null))

// DISPATCH ====================================================================

// Per-FieldType dispatch. Adding a new reference type means adding one
// entry here. Each entry exposes a reactive computed `groups` (so store
// hydration / field-subtype changes flow through) plus a `parse` for the
// <select>'s string-typed value.
//
// Lazily built inside a computed so Pinia stores are only touched when
// the component actually renders.
const TYPE_DISPATCH = computed<Record<string, TypeDispatch>>(() => ({
  schema: {
    placeholder: "Select schema...",
    parse: (raw) => (raw === "" ? null : Number(raw)),
    groups: schemaGroups.value,
  },
  type: {
    placeholder: "Select type...",
    parse: (raw) => (raw === "" ? null : raw),
    groups: typeGroups.value,
    icon: (value) => (typeof value === "string" ? getTypeIcon(value) : null),
  },
  user: {
    placeholder: "Select user...",
    parse: (raw) => (raw === "" ? null : Number(raw)),
    groups: userGroups.value,
  },
}))

const dispatch = computed<TypeDispatch>(() => {
  const entry = props.field?.type && TYPE_DISPATCH.value[props.field.type]
  // `schema` is the always-present default dispatch (see TYPE_DISPATCH literal).
  return entry || TYPE_DISPATCH.value.schema!
})

const placeholder = computed(() => props.placeholder ?? dispatch.value.placeholder)

const leadingIcon = computed<string | null>(() => {
  const fn = dispatch.value.icon
  if (!fn) return null
  return fn(props.modelValue)
})

// SCHEMA ======================================================================

const { schemas } = storeToRefs(useSchemasStore())
const typesStore = useTypesStore()

// Scope the schema picker to one record type and exclude that type's
// base schema (its fields are inherited automatically by every record
// of the type). The record type comes from `field.recordType` when the
// schema field is declared with a fixed type, or from `item.type` when
// the picker sits on a `schema_id` cell — each row of a records grid
// may be a different type, so the cell's record type is row-dependent.
const schemaRecordType = computed<string | null>(() => {
  const fromField = props.field?.recordType
  if (fromField) return fromField
  if (props.field?.key === "schema_id") {
    const fromItem = props.item?.type
    if (typeof fromItem === "string") return fromItem
  }
  return null
})

const schemaTypeSchemaId = computed(() => {
  const recordType = schemaRecordType.value
  if (!recordType) return null
  return typesStore.get(recordType)?.schema_id ?? null
})

const schemaGroups = computed<OptionGroup[]>(() => {
  const recordType = schemaRecordType.value
  if (recordType) {
    const filtered = schemas.value.filter(
      (s) => s.record_type === recordType && s.id !== schemaTypeSchemaId.value
    )
    return [
      {
        key: "schemas",
        label: null,
        options: filtered.map((s) => ({ value: s.id, label: s.name })),
      },
    ]
  }

  const byType = new Map<string, typeof schemas.value>()
  for (const schema of schemas.value) {
    const t = schema.record_type || "untyped"
    if (!byType.has(t)) byType.set(t, [])
    byType.get(t)!.push(schema)
  }

  const result: OptionGroup[] = []
  for (const type of typesStore.types) {
    const list = byType.get(type.name)
    if (!list?.length) continue
    result.push({
      key: type.name,
      label: type.plural_label,
      options: list.map((s) => ({ value: s.id, label: s.name })),
    })
  }
  const untyped = byType.get("untyped")
  if (untyped?.length) {
    result.push({
      key: "untyped",
      label: "Untyped Schemas",
      options: untyped.map((s) => ({ value: s.id, label: s.name })),
    })
  }
  return result
})

// TYPE ========================================================================

const typeGroups = computed<OptionGroup[]>(() => [
  {
    key: "types",
    label: null,
    options: typesStore.types.map((t) => ({ value: t.name, label: t.label })),
  },
])

// USER ========================================================================

const usersStore = useUsersStore()
const { users } = storeToRefs(usersStore)

const userGroups = computed<OptionGroup[]>(() => {
  const active = usersStore.activeUsers ?? users.value
  return [
    {
      key: "users",
      label: null,
      options: active
        .map((u) => ({ value: u.id as number, label: (u.name as string) || (u.email as string) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
  ]
})

// EVENTS ======================================================================

function handleChange(event: Event) {
  const raw = (event.target as HTMLSelectElement).value
  emit("update:modelValue", dispatch.value.parse(raw))
}
</script>

<style>
.reference-select {
  width: 100%;
  position: relative;

  .select,
  select {
    width: 100%;
  }

  > .icon.is-left {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 2em;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 1;
  }

  select.has-leading-icon {
    padding-inline-start: 2em;
  }

  .select select {
    border-color: transparent;
    box-shadow: none;

    &:not([multiple]) {
      padding-inline-end: 0;
    }
  }

  .select.is-small::after {
    opacity: 0;
  }

  &:hover {
    select {
      border-color: var(--auger-input-border);

      &:not([multiple]) {
        padding-inline-end: 2.5em;
      }
    }

    .select.is-small::after {
      opacity: 1;
    }
  }

  select:not([multiple]):focus {
    padding-inline-end: 2.5em;
  }

  select.has-placeholder {
    color: var(--auger-text-weak);
  }
}
</style>
